import { formatDuration } from "@/lib/api";
import { toCanonicalMeters, formatStoredDistance } from "@/lib/units";

// target = what the coach fixes per interval; log = what the athlete records.
export const INTERVAL_GOALS = {
  time_for_distance:     { label: "Time for Distance",     target: "distance", log: "time",     hint: "Cover a set distance — log how long it took." },
  time_for_calories:     { label: "Time for Calories",     target: "calories", log: "time",     hint: "Hit a calorie target — log how long it took." },
  calories_for_time:     { label: "Calories for Time",     target: "time",     log: "calories", hint: "Fixed work time — log max calories." },
  distance_for_time:     { label: "Distance for Time",     target: "time",     log: "distance", hint: "Fixed work time — log max distance." },
  distance_for_calories: { label: "Distance for Calories", target: "calories", log: "distance", hint: "Work until a calorie target — log distance covered." },
};

export const DEFAULT_INTERVAL_GOAL = "time_for_distance";

export const goalOf = (sc) => INTERVAL_GOALS[sc?.interval_goal] || INTERVAL_GOALS[DEFAULT_INTERVAL_GOAL];

export const toSeconds = (min, sec) => Number(min || 0) * 60 + Number(sec || 0);

export function splitSeconds(total) {
  if (total == null || total === "") return { min: "", sec: "" };
  const t = Math.max(0, Math.round(Number(total)));
  return { min: Math.floor(t / 60), sec: t % 60 };
}

export function describeIntervals(sc = {}) {
  const goal = goalOf(sc);
  const n = sc.rounds || "?";
  let target;
  if (goal.target === "distance") target = sc.target_distance_meters ? formatStoredDistance(sc.target_distance_meters, sc.target_distance_unit || "m") : "distance";
  else if (goal.target === "calories") target = sc.target_calories ? `${sc.target_calories} cal` : "cal";
  else target = sc.target_time_seconds ? formatDuration(sc.target_time_seconds) : "time";
  const verb = { time: "for time", calories: "max cal", distance: "max distance" }[goal.log];
  const parts = [`${n} × ${target} ${verb}`];
  if (goal.target !== "time" && sc.target_time_seconds) parts.push(`work ${formatDuration(sc.target_time_seconds)}`);
  if (sc.rest_seconds) parts.push(`rest ${formatDuration(sc.rest_seconds)}`);
  return parts.join(" · ");
}

const num = (v) => (v === "" || v == null ? null : Number(v) > 0 ? Number(v) : null);

// Row shape: { min, sec, dist, cal } — every metric can be entered for every interval.
export const rowTime = (r) => toSeconds(r.min, r.sec) || null;

// Values of the goal's primary (scored) metric, one per row.
export function rowValues(goal, rows) {
  return rows.map((r) => (goal.log === "time" ? rowTime(r) : goal.log === "calories" ? num(r.cal) : num(r.dist)));
}

export function summarizeIntervals(goal, rows) {
  const vals = rowValues(goal, rows).filter((v) => v != null);
  if (!vals.length) return null;
  const total = vals.reduce((a, b) => a + b, 0);
  const best = goal.log === "time" ? Math.min(...vals) : Math.max(...vals);
  return { count: vals.length, total, avg: total / vals.length, best };
}

export function formatIntervalValue(goal, v, distanceUnit = "m") {
  if (v == null) return "—";
  if (goal.log === "time") return formatDuration(v);
  if (goal.log === "calories") return `${Math.round(v * 10) / 10} cal`;
  return `${Math.round(v * 10) / 10} ${distanceUnit}`;
}

// Build the workout-log payload fields from the per-interval rows.
export function buildIntervalPayload(goal, rows, distanceUnit = "m") {
  const results = rows
    .map((r) => {
      const t = rowTime(r), d = num(r.dist), c = num(r.cal);
      if (!t && !d && !c) return null;
      return { time_seconds: t, distance_meters: d ? toCanonicalMeters(d, distanceUnit) : null, calories: c };
    })
    .filter(Boolean);
  const total = (k) => {
    const v = results.reduce((a, r) => a + (r[k] || 0), 0);
    return v > 0 ? Math.round(v * 100) / 100 : null;
  };
  return {
    interval_results: results,
    total_time_seconds: total("time_seconds"),
    calories: total("calories"),
    distance_meters: total("distance_meters"),
    distance_unit: distanceUnit,
  };
}

// Human-readable split for one stored interval result.
export function formatSplit(r, distanceUnit = "m") {
  return [
    r.time_seconds ? formatDuration(r.time_seconds) : null,
    r.distance_meters ? formatStoredDistance(r.distance_meters, distanceUnit) : null,
    r.calories ? `${r.calories} cal` : null,
  ].filter(Boolean).join(", ");
}
